import { CarrierProductionFileId } from './carrier-production-raw-carrier-file.model';
import { BaseModel } from '../base.model';
import {
  REPORTING_COLLECTION,
  REPORTING_DB_NAME,
  REPORTING_DB_SCHEMA,
  REPORTING_IMPORTS_COLLECTION,
  REPORTING_MODIFIED_CARRIER_FILES_DIRECTORY,
  REPORTING_RAW_CARRIER_FILES_COLLECTION,
  REPORTING_RAW_CARRIER_FILE_INVALID_ROWS_SUBCOLLECTION,
  REPORTING_RAW_CARRIER_FILE_PROCESSING_RUNS_SUBCOLLECTION,
  ReportingDbTableName,
} from './reporting-db.model';

export {
  REPORTING_COLLECTION,
  REPORTING_DB_NAME,
  REPORTING_DB_SCHEMA,
  REPORTING_IMPORTS_COLLECTION,
  REPORTING_MODIFIED_CARRIER_FILES_DIRECTORY,
  REPORTING_RAW_CARRIER_FILES_COLLECTION,
  REPORTING_RAW_CARRIER_FILE_INVALID_ROWS_SUBCOLLECTION,
  REPORTING_RAW_CARRIER_FILE_PROCESSING_RUNS_SUBCOLLECTION,
  ReportingDbTableName,
} from './reporting-db.model';

export enum ReportingImportRunStatus {
  Uploaded = 'uploaded',
  ValidatingStructure = 'validating-structure',
  CountingRecords = 'counting-records',
  Normalizing = 'normalizing',
  ValidatingRows = 'validating-rows',
  NeedsUserInput = 'needs-user-input',
  BuildingFinalFile = 'building-final-file',
  Completed = 'completed',
  Failed = 'failed',
}

export const REPORTING_IMPORT_RUN_STATUSES = Object.values(ReportingImportRunStatus) as ReportingImportRunStatus[];

export enum ReportingImportRunFailureKind {
  StructureCheck = 'structure-check',
}

export const REPORTING_IMPORT_RUN_FAILURE_KINDS = Object.values(
  ReportingImportRunFailureKind,
) as ReportingImportRunFailureKind[];

export enum ReportingImportRowStatus {
  Parsed = 'parsed',
  Valid = 'valid',
  Invalid = 'invalid',
  Resolved = 'resolved',
  IncludedInFinal = 'included-in-final',
}

export const REPORTING_IMPORT_ROW_STATUSES = Object.values(ReportingImportRowStatus) as ReportingImportRowStatus[];

export enum ReportingImportRowStepStatus {
  Processed = 'processed',
  Blocked = 'blocked',
  NeedsUserInput = 'needs-user-input',
  Skipped = 'skipped',
}

export const REPORTING_IMPORT_ROW_STEP_STATUSES = Object.values(
  ReportingImportRowStepStatus,
) as ReportingImportRowStepStatus[];

export enum ReportingImportRunKeys {
  id = 'id',
  reportDate = 'report_date',
  carrierFileId = 'carrier_file_id',
  uploadActionId = 'upload_action_id',
  rawFilePath = 'raw_file_path',
  rawFileName = 'raw_file_name',
  rawFileContentType = 'raw_file_content_type',
  finalFilePath = 'final_file_path',
  finalFileName = 'final_file_name',
  finalFileContentType = 'final_file_content_type',
  status = 'status',
  failureKind = 'failure_kind',
  processingErrorMessage = 'processing_error_message',
  recordsTotal = 'records_total',
  recordsValid = 'records_valid',
  recordsInvalid = 'records_invalid',
  recordsResolved = 'records_resolved',
  startedAt = 'started_at',
  completedAt = 'completed_at',
  createdAt = 'created_at',
  updatedAt = 'updated_at',
}

export enum ReportingImportStateKeys {
  createdDate = 'created_date',
  updatedDate = 'updated_date',
  submitImportReady = 'submitImportReady',
  submitImportRowsCount = 'submitImportRowsCount',
  submitImportUpdatedAt = 'submitImportUpdatedAt',
  submitImportRunId = 'submitImportRunId',
}

export interface ReportingImportState extends BaseModel {
  [ReportingImportStateKeys.submitImportReady]?: boolean | null;
  [ReportingImportStateKeys.submitImportRowsCount]?: number | null;
  [ReportingImportStateKeys.submitImportUpdatedAt]?: string | Date | null;
  [ReportingImportStateKeys.submitImportRunId]?: string | null;
}

export enum ReportingImportRowKeys {
  id = 'id',
  runId = 'run_id',
  rowNumber = 'row_number',
  status = 'status',
  rawPayload = 'raw_payload',
  normalizedPayload = 'normalized_payload',
  correctedPayload = 'corrected_payload',
  finalPayload = 'final_payload',
  normalizationStepStatuses = 'normalization_step_statuses',
  validationErrors = 'validation_errors',
  isUserResolved = 'is_user_resolved',
  needsUserInput = 'needs_user_input',
  includedInFinal = 'included_in_final',
  createdAt = 'created_at',
  updatedAt = 'updated_at',
}

export enum ReportingImportNormalizationIssueType {
  Conflict = 'conflict',
  Unresolved = 'unresolved',
}

export const REPORTING_IMPORT_NORMALIZATION_ISSUE_TYPES = Object.values(
  ReportingImportNormalizationIssueType,
) as ReportingImportNormalizationIssueType[];

export enum ReportingImportNormalizationMatchMode {
  Exact = 'exact',
  Fuzzy = 'fuzzy',
  None = 'none',
}

export const REPORTING_IMPORT_NORMALIZATION_MATCH_MODES = Object.values(
  ReportingImportNormalizationMatchMode,
) as ReportingImportNormalizationMatchMode[];

export enum CarrierNormalizationRuleKey {
  ForestersApplyAgentNameToProdName = 'foresters.apply-agent-name-to-prod-name',
  ForestersApplyAgentMgaCorrections = 'foresters.apply-agent-mga-corrections',
  ForestersApplyMgaTableOverrides = 'foresters.apply-mga-table-overrides',
  ForestersReplaceReportAgentNameByMgaAndFileAgent = 'foresters.replace-report-agent-name-by-mga-and-file-agent',
  ForestersApplyGpmForTagAgencies = 'foresters.apply-gpm-for-tag-agencies',
  ForestersApplyCarrierOverrides = 'foresters.apply-carrier-overrides',
  ForestersApplyNtpTerminatedAgentOverrides = 'foresters.apply-ntp-terminated-agent-overrides',
  ForestersValidateRequiredNormalizedFields = 'foresters.validate-required-normalized-fields',
  ForestersGenerateExactMatchIssues = 'foresters.generate-exact-match-issues',
  ForestersGenerateFuzzyMatchCandidates = 'foresters.generate-fuzzy-match-candidates',
  ForestersMarkUnresolvedNormalizationIssues = 'foresters.mark-unresolved-normalization-issues',
  NlgSubmitValidateRequiredNormalizedFields = 'nlg-submit.validate-required-normalized-fields',
  NlgSubmitApplyAgentNameToProdName = 'nlg-submit.apply-agent-name-to-prod-name',
  NlgSubmitApplyAgentMgaCorrections = 'nlg-submit.apply-agent-mga-corrections',
  NlgSubmitApplyMgaTableOverrides = 'nlg-submit.apply-mga-table-overrides',
  NlgSubmitReplaceReportAgentNameByMgaAndFileAgent = 'nlg-submit.replace-report-agent-name-by-mga-and-file-agent',
  NlgSubmitApplyGpmForTagAgencies = 'nlg-submit.apply-gpm-for-tag-agencies',
  NlgSubmitApplyCarrierOverrides = 'nlg-submit.apply-carrier-overrides',
  NlgSubmitApplyNtpTerminatedAgentOverrides = 'nlg-submit.apply-ntp-terminated-agent-overrides',
  AimcorSubmitValidateRequiredNormalizedFields = 'aimcor-submit.validate-required-normalized-fields',
  AimcorSubmitApplyAgentNameToProdName = 'aimcor-submit.apply-agent-name-to-prod-name',
  AimcorSubmitApplyAgentMgaCorrections = 'aimcor-submit.apply-agent-mga-corrections',
  AimcorSubmitApplyMgaTableOverrides = 'aimcor-submit.apply-mga-table-overrides',
  AimcorSubmitReplaceReportAgentNameByMgaAndFileAgent = 'aimcor-submit.replace-report-agent-name-by-mga-and-file-agent',
  AimcorSubmitApplyCarrierOverrides = 'aimcor-submit.apply-carrier-overrides',
  AimcorSubmitApplyNtpTerminatedAgentOverrides = 'aimcor-submit.apply-ntp-terminated-agent-overrides',
  AnicoSubmitValidateRequiredNormalizedFields = 'anico-submit.validate-required-normalized-fields',
  AnicoSubmitApplyAgentNameToProdName = 'anico-submit.apply-agent-name-to-prod-name',
  AnicoSubmitApplyAgentMgaCorrections = 'anico-submit.apply-agent-mga-corrections',
  AnicoSubmitApplyMgaTableOverrides = 'anico-submit.apply-mga-table-overrides',
  AnicoSubmitReplaceReportAgentNameByMgaAndFileAgent = 'anico-submit.replace-report-agent-name-by-mga-and-file-agent',
  EquisSubmitValidateRequiredNormalizedFields = 'equis-submit.validate-required-normalized-fields',
  EquisSubmitApplyAgentNameToProdName = 'equis-submit.apply-agent-name-to-prod-name',
  EquisSubmitApplyAgentMgaCorrections = 'equis-submit.apply-agent-mga-corrections',
  EquisSubmitApplyMgaTableOverrides = 'equis-submit.apply-mga-table-overrides',
  EquisSubmitReplaceReportAgentNameByMgaAndFileAgent = 'equis-submit.replace-report-agent-name-by-mga-and-file-agent',
  EquisSubmitApplyCarrierOverrides = 'equis-submit.apply-carrier-overrides',
  EquisSubmitApplyNtpTerminatedAgentOverrides = 'equis-submit.apply-ntp-terminated-agent-overrides',
}

export const CARRIER_NORMALIZATION_RULE_KEYS = Object.values(
  CarrierNormalizationRuleKey,
) as CarrierNormalizationRuleKey[];

/**
 * Carrier-agnostic normalization rule *type* — the shared step a rule performs, independent of which
 * carrier ran it. Every carrier chain re-uses the same rule processors; a `CarrierNormalizationRuleKey`
 * is a carrier-namespaced instance of one of these types (e.g. `equis-submit.apply-mga-table-overrides`
 * and `foresters.apply-mga-table-overrides` are both `ApplyMgaTableOverrides`). Consumers that care
 * about *what* a step does (labels, tones, ordering, resolve instructions) key off the rule type; only
 * identity / persistence / resolution matching keys off the full `CarrierNormalizationRuleKey`.
 */
export enum CarrierNormalizationRuleType {
  ValidateRequiredNormalizedFields = 'validate-required-normalized-fields',
  ApplyAgentNameToProdName = 'apply-agent-name-to-prod-name',
  ApplyAgentMgaCorrections = 'apply-agent-mga-corrections',
  ApplyMgaTableOverrides = 'apply-mga-table-overrides',
  ReplaceReportAgentNameByMgaAndFileAgent = 'replace-report-agent-name-by-mga-and-file-agent',
  ApplyGpmForTagAgencies = 'apply-gpm-for-tag-agencies',
  ApplyCarrierOverrides = 'apply-carrier-overrides',
  ApplyNtpTerminatedAgentOverrides = 'apply-ntp-terminated-agent-overrides',
  GenerateExactMatchIssues = 'generate-exact-match-issues',
  GenerateFuzzyMatchCandidates = 'generate-fuzzy-match-candidates',
  MarkUnresolvedNormalizationIssues = 'mark-unresolved-normalization-issues',
}

/**
 * Authoritative rule-key → rule-type mapping. Declared explicitly (not derived by string-splitting the
 * key) so the relationship is a first-class, exhaustive, type-checked property: adding a new
 * `CarrierNormalizationRuleKey` without classifying it here is a compile error.
 */
export const CARRIER_NORMALIZATION_RULE_TYPE_BY_KEY: Record<
  CarrierNormalizationRuleKey,
  CarrierNormalizationRuleType
> = {
  // Foresters
  [CarrierNormalizationRuleKey.ForestersValidateRequiredNormalizedFields]:
    CarrierNormalizationRuleType.ValidateRequiredNormalizedFields,
  [CarrierNormalizationRuleKey.ForestersApplyAgentNameToProdName]: CarrierNormalizationRuleType.ApplyAgentNameToProdName,
  [CarrierNormalizationRuleKey.ForestersApplyAgentMgaCorrections]: CarrierNormalizationRuleType.ApplyAgentMgaCorrections,
  [CarrierNormalizationRuleKey.ForestersApplyMgaTableOverrides]: CarrierNormalizationRuleType.ApplyMgaTableOverrides,
  [CarrierNormalizationRuleKey.ForestersReplaceReportAgentNameByMgaAndFileAgent]:
    CarrierNormalizationRuleType.ReplaceReportAgentNameByMgaAndFileAgent,
  [CarrierNormalizationRuleKey.ForestersApplyGpmForTagAgencies]: CarrierNormalizationRuleType.ApplyGpmForTagAgencies,
  [CarrierNormalizationRuleKey.ForestersApplyCarrierOverrides]: CarrierNormalizationRuleType.ApplyCarrierOverrides,
  [CarrierNormalizationRuleKey.ForestersApplyNtpTerminatedAgentOverrides]:
    CarrierNormalizationRuleType.ApplyNtpTerminatedAgentOverrides,
  [CarrierNormalizationRuleKey.ForestersGenerateExactMatchIssues]: CarrierNormalizationRuleType.GenerateExactMatchIssues,
  [CarrierNormalizationRuleKey.ForestersGenerateFuzzyMatchCandidates]:
    CarrierNormalizationRuleType.GenerateFuzzyMatchCandidates,
  [CarrierNormalizationRuleKey.ForestersMarkUnresolvedNormalizationIssues]:
    CarrierNormalizationRuleType.MarkUnresolvedNormalizationIssues,
  // NLG submit
  [CarrierNormalizationRuleKey.NlgSubmitValidateRequiredNormalizedFields]:
    CarrierNormalizationRuleType.ValidateRequiredNormalizedFields,
  [CarrierNormalizationRuleKey.NlgSubmitApplyAgentNameToProdName]: CarrierNormalizationRuleType.ApplyAgentNameToProdName,
  [CarrierNormalizationRuleKey.NlgSubmitApplyAgentMgaCorrections]: CarrierNormalizationRuleType.ApplyAgentMgaCorrections,
  [CarrierNormalizationRuleKey.NlgSubmitApplyMgaTableOverrides]: CarrierNormalizationRuleType.ApplyMgaTableOverrides,
  [CarrierNormalizationRuleKey.NlgSubmitReplaceReportAgentNameByMgaAndFileAgent]:
    CarrierNormalizationRuleType.ReplaceReportAgentNameByMgaAndFileAgent,
  [CarrierNormalizationRuleKey.NlgSubmitApplyGpmForTagAgencies]: CarrierNormalizationRuleType.ApplyGpmForTagAgencies,
  [CarrierNormalizationRuleKey.NlgSubmitApplyCarrierOverrides]: CarrierNormalizationRuleType.ApplyCarrierOverrides,
  [CarrierNormalizationRuleKey.NlgSubmitApplyNtpTerminatedAgentOverrides]:
    CarrierNormalizationRuleType.ApplyNtpTerminatedAgentOverrides,
  // Aimcor submit
  [CarrierNormalizationRuleKey.AimcorSubmitValidateRequiredNormalizedFields]:
    CarrierNormalizationRuleType.ValidateRequiredNormalizedFields,
  [CarrierNormalizationRuleKey.AimcorSubmitApplyAgentNameToProdName]:
    CarrierNormalizationRuleType.ApplyAgentNameToProdName,
  [CarrierNormalizationRuleKey.AimcorSubmitApplyAgentMgaCorrections]:
    CarrierNormalizationRuleType.ApplyAgentMgaCorrections,
  [CarrierNormalizationRuleKey.AimcorSubmitApplyMgaTableOverrides]: CarrierNormalizationRuleType.ApplyMgaTableOverrides,
  [CarrierNormalizationRuleKey.AimcorSubmitReplaceReportAgentNameByMgaAndFileAgent]:
    CarrierNormalizationRuleType.ReplaceReportAgentNameByMgaAndFileAgent,
  [CarrierNormalizationRuleKey.AimcorSubmitApplyCarrierOverrides]: CarrierNormalizationRuleType.ApplyCarrierOverrides,
  [CarrierNormalizationRuleKey.AimcorSubmitApplyNtpTerminatedAgentOverrides]:
    CarrierNormalizationRuleType.ApplyNtpTerminatedAgentOverrides,
  // ANICO submit
  [CarrierNormalizationRuleKey.AnicoSubmitValidateRequiredNormalizedFields]:
    CarrierNormalizationRuleType.ValidateRequiredNormalizedFields,
  [CarrierNormalizationRuleKey.AnicoSubmitApplyAgentNameToProdName]:
    CarrierNormalizationRuleType.ApplyAgentNameToProdName,
  [CarrierNormalizationRuleKey.AnicoSubmitApplyAgentMgaCorrections]:
    CarrierNormalizationRuleType.ApplyAgentMgaCorrections,
  [CarrierNormalizationRuleKey.AnicoSubmitApplyMgaTableOverrides]: CarrierNormalizationRuleType.ApplyMgaTableOverrides,
  [CarrierNormalizationRuleKey.AnicoSubmitReplaceReportAgentNameByMgaAndFileAgent]:
    CarrierNormalizationRuleType.ReplaceReportAgentNameByMgaAndFileAgent,
  // Equis submit
  [CarrierNormalizationRuleKey.EquisSubmitValidateRequiredNormalizedFields]:
    CarrierNormalizationRuleType.ValidateRequiredNormalizedFields,
  [CarrierNormalizationRuleKey.EquisSubmitApplyAgentNameToProdName]:
    CarrierNormalizationRuleType.ApplyAgentNameToProdName,
  [CarrierNormalizationRuleKey.EquisSubmitApplyAgentMgaCorrections]:
    CarrierNormalizationRuleType.ApplyAgentMgaCorrections,
  [CarrierNormalizationRuleKey.EquisSubmitApplyMgaTableOverrides]: CarrierNormalizationRuleType.ApplyMgaTableOverrides,
  [CarrierNormalizationRuleKey.EquisSubmitReplaceReportAgentNameByMgaAndFileAgent]:
    CarrierNormalizationRuleType.ReplaceReportAgentNameByMgaAndFileAgent,
  [CarrierNormalizationRuleKey.EquisSubmitApplyCarrierOverrides]: CarrierNormalizationRuleType.ApplyCarrierOverrides,
  [CarrierNormalizationRuleKey.EquisSubmitApplyNtpTerminatedAgentOverrides]:
    CarrierNormalizationRuleType.ApplyNtpTerminatedAgentOverrides,
};

/** Resolve the carrier-agnostic rule type for a namespaced normalization rule key. */
export function getCarrierNormalizationRuleType(ruleKey: CarrierNormalizationRuleKey): CarrierNormalizationRuleType {
  return CARRIER_NORMALIZATION_RULE_TYPE_BY_KEY[ruleKey];
}

export enum ReportingImportNormalizationIssueKeys {
  id = 'id',
  runId = 'run_id',
  rowNumber = 'row_number',
  ruleKey = 'rule_key',
  issueType = 'issue_type',
  fieldName = 'field_name',
  sourceValue = 'source_value',
  suggestedValue = 'suggested_value',
  matchMode = 'match_mode',
  details = 'details',
  candidateCount = 'candidate_count',
  isResolved = 'is_resolved',
  createdAt = 'created_at',
  updatedAt = 'updated_at',
}

export enum ReportingImportNormalizationIssueCandidateKeys {
  id = 'id',
  issueId = 'issue_id',
  candidateRank = 'candidate_rank',
  candidateKey = 'candidate_key',
  candidateValue = 'candidate_value',
  matchScore = 'match_score',
  details = 'details',
  createdAt = 'created_at',
}

export interface ReportingImportValidationError {
  code: string;
  message: string;
  field?: string | null;
  actualValue?: unknown;
  suggestedValue?: unknown;
  isBlocking?: boolean;
}

export type ReportingImportRowPayload = Record<string, unknown>;
export type ReportingImportRowStepStatuses = Partial<
  Record<CarrierNormalizationRuleKey, ReportingImportRowStepStatus>
>;

export interface ReportingImportNormalizationIssueCandidatePayload {
  candidateRank?: number;
  candidateKey?: string | null;
  candidateValue?: string | null;
  matchScore?: number | null;
  details?: Record<string, unknown> | null;
}

export interface ReportingImportNormalizationIssuePayload {
  rowNumber: number;
  ruleKey: CarrierNormalizationRuleKey;
  issueType: ReportingImportNormalizationIssueType;
  fieldName?: string | null;
  sourceValue?: string | null;
  suggestedValue?: string | null;
  matchMode?: ReportingImportNormalizationMatchMode | null;
  details?: Record<string, unknown> | null;
  candidateCount?: number | null;
  isResolved?: boolean;
  candidates?: ReportingImportNormalizationIssueCandidatePayload[];
}

export interface ReportingImportRun {
  [ReportingImportRunKeys.id]: string;
  [ReportingImportRunKeys.reportDate]: string;
  [ReportingImportRunKeys.carrierFileId]: CarrierProductionFileId;
  [ReportingImportRunKeys.uploadActionId]: string;
  [ReportingImportRunKeys.rawFilePath]: string;
  [ReportingImportRunKeys.rawFileName]: string;
  [ReportingImportRunKeys.rawFileContentType]?: string | null;
  [ReportingImportRunKeys.finalFilePath]?: string | null;
  [ReportingImportRunKeys.finalFileName]?: string | null;
  [ReportingImportRunKeys.finalFileContentType]?: string | null;
  [ReportingImportRunKeys.status]: ReportingImportRunStatus;
  [ReportingImportRunKeys.failureKind]?: ReportingImportRunFailureKind | null;
  [ReportingImportRunKeys.processingErrorMessage]?: string | null;
  [ReportingImportRunKeys.recordsTotal]: number;
  [ReportingImportRunKeys.recordsValid]: number;
  [ReportingImportRunKeys.recordsInvalid]: number;
  [ReportingImportRunKeys.recordsResolved]: number;
  [ReportingImportRunKeys.startedAt]?: Date | string | null;
  [ReportingImportRunKeys.completedAt]?: Date | string | null;
  [ReportingImportRunKeys.createdAt]: Date | string;
  [ReportingImportRunKeys.updatedAt]: Date | string;
}

export interface ReportingImportRow {
  [ReportingImportRowKeys.id]: string;
  [ReportingImportRowKeys.runId]: string;
  [ReportingImportRowKeys.rowNumber]: number;
  [ReportingImportRowKeys.status]: ReportingImportRowStatus;
  [ReportingImportRowKeys.rawPayload]: ReportingImportRowPayload;
  [ReportingImportRowKeys.normalizedPayload]?: ReportingImportRowPayload | null;
  [ReportingImportRowKeys.correctedPayload]?: ReportingImportRowPayload | null;
  [ReportingImportRowKeys.finalPayload]?: ReportingImportRowPayload | null;
  [ReportingImportRowKeys.normalizationStepStatuses]?: ReportingImportRowStepStatuses;
  [ReportingImportRowKeys.validationErrors]: ReportingImportValidationError[];
  [ReportingImportRowKeys.isUserResolved]: boolean;
  [ReportingImportRowKeys.needsUserInput]: boolean;
  [ReportingImportRowKeys.includedInFinal]: boolean;
  [ReportingImportRowKeys.createdAt]: Date | string;
  [ReportingImportRowKeys.updatedAt]: Date | string;
}

export interface ReportingImportNormalizationIssueCandidate {
  [ReportingImportNormalizationIssueCandidateKeys.id]: string;
  [ReportingImportNormalizationIssueCandidateKeys.issueId]: string;
  [ReportingImportNormalizationIssueCandidateKeys.candidateRank]: number;
  [ReportingImportNormalizationIssueCandidateKeys.candidateKey]?: string | null;
  [ReportingImportNormalizationIssueCandidateKeys.candidateValue]?: string | null;
  [ReportingImportNormalizationIssueCandidateKeys.matchScore]?: number | null;
  [ReportingImportNormalizationIssueCandidateKeys.details]: Record<string, unknown>;
  [ReportingImportNormalizationIssueCandidateKeys.createdAt]: Date | string;
}

export interface ReportingImportNormalizationIssue {
  [ReportingImportNormalizationIssueKeys.id]: string;
  [ReportingImportNormalizationIssueKeys.runId]: string;
  [ReportingImportNormalizationIssueKeys.rowNumber]: number;
  [ReportingImportNormalizationIssueKeys.ruleKey]: CarrierNormalizationRuleKey;
  [ReportingImportNormalizationIssueKeys.issueType]: ReportingImportNormalizationIssueType;
  [ReportingImportNormalizationIssueKeys.fieldName]?: string | null;
  [ReportingImportNormalizationIssueKeys.sourceValue]?: string | null;
  [ReportingImportNormalizationIssueKeys.suggestedValue]?: string | null;
  [ReportingImportNormalizationIssueKeys.matchMode]?: ReportingImportNormalizationMatchMode | null;
  [ReportingImportNormalizationIssueKeys.details]: Record<string, unknown>;
  [ReportingImportNormalizationIssueKeys.candidateCount]: number;
  [ReportingImportNormalizationIssueKeys.isResolved]: boolean;
  [ReportingImportNormalizationIssueKeys.createdAt]: Date | string;
  [ReportingImportNormalizationIssueKeys.updatedAt]: Date | string;
}

export enum CarrierProductionImportHistoryStatus {
  NotStarted = 'not-started',
  Uploaded = 'uploaded',
  Processing = 'processing',
  NeedsUserInput = 'needs-user-input',
  Completed = 'completed',
  Closed = 'closed',
  Failed = 'failed',
}

export enum ReportingImportHistorySourceType {
  Carrier = 'carrier',
  Master = 'master',
}

export enum ReportingImportHistoryKeys {
  reportDate = 'reportDate',
  year = 'year',
  status = 'status',
  manuallyClosed = 'manuallyClosed',
  manuallyClosedAt = 'manuallyClosedAt',
  metrics = 'metrics',
  byType = 'byType',
  updatedAt = 'updatedAt',
}

export enum ReportingImportHistoryMetricsKeys {
  totalTypes = 'totalTypes',
  uploadedTypes = 'uploadedTypes',
  checkedTypes = 'checkedTypes',
  processedTypes = 'processedTypes',
  completionPercent = 'completionPercent',
}

export enum ReportingImportHistoryTypeStateKeys {
  key = 'key',
  source = 'source',
  status = 'status',
  uploaded = 'uploaded',
  checked = 'checked',
  processed = 'processed',
  updatedAt = 'updatedAt',
}

export interface ReportingImportHistoryMetrics {
  [ReportingImportHistoryMetricsKeys.totalTypes]: number;
  [ReportingImportHistoryMetricsKeys.uploadedTypes]: number;
  [ReportingImportHistoryMetricsKeys.checkedTypes]: number;
  [ReportingImportHistoryMetricsKeys.processedTypes]: number;
  [ReportingImportHistoryMetricsKeys.completionPercent]: number;
}

export interface ReportingImportHistoryTypeState {
  [ReportingImportHistoryTypeStateKeys.key]: string;
  [ReportingImportHistoryTypeStateKeys.source]: ReportingImportHistorySourceType;
  [ReportingImportHistoryTypeStateKeys.status]: CarrierProductionImportHistoryStatus;
  [ReportingImportHistoryTypeStateKeys.uploaded]: boolean;
  [ReportingImportHistoryTypeStateKeys.checked]: boolean;
  [ReportingImportHistoryTypeStateKeys.processed]: boolean;
  [ReportingImportHistoryTypeStateKeys.updatedAt]?: Date | string | null;
}

export interface ReportingImportHistoryRecord extends BaseModel {
  [ReportingImportHistoryKeys.reportDate]: string;
  [ReportingImportHistoryKeys.year]: string;
  [ReportingImportHistoryKeys.status]: CarrierProductionImportHistoryStatus;
  [ReportingImportHistoryKeys.manuallyClosed]?: boolean;
  [ReportingImportHistoryKeys.manuallyClosedAt]?: Date | string | null;
  [ReportingImportHistoryKeys.metrics]: ReportingImportHistoryMetrics;
  [ReportingImportHistoryKeys.byType]: Record<string, ReportingImportHistoryTypeState>;
  [ReportingImportHistoryKeys.updatedAt]?: Date | string | null;
}
